import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserService } from '../../../services/user.service';
import { User } from '../../../models/user.models';
import { httpErrorMessage } from '../../../core/http-error';

@Component({
  selector: 'app-user-detail',
  imports: [DatePipe, RouterLink, FormsModule],
  templateUrl: './user-detail.component.html',
  styleUrl: './user-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDetailComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly user = signal<User | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);

  /** Editable copy of the user's name. */
  readonly name = signal('');
  readonly saving = signal(false);
  readonly deleting = signal(false);
  readonly actionError = signal<string | null>(null);

  private id = '';

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    this.userService.getUser(this.id).subscribe({
      next: u => {
        this.user.set(u);
        this.name.set(u.name ?? '');
        this.loading.set(false);
      },
      error: err => {
        this.error.set(httpErrorMessage(err, 'Failed to load user.'));
        this.loading.set(false);
      },
    });
  }

  save(): void {
    this.saving.set(true);
    this.actionError.set(null);
    this.userService.updateUser(this.id, { name: this.name() }).subscribe({
      next: u => {
        this.user.set(u);
        this.name.set(u.name ?? '');
        this.saving.set(false);
      },
      error: err => {
        this.actionError.set(httpErrorMessage(err, 'Failed to save changes.'));
        this.saving.set(false);
      },
    });
  }

  remove(): void {
    if (!confirm('Delete this user? This cannot be undone.')) return;
    this.deleting.set(true);
    this.actionError.set(null);
    this.userService.deleteUser(this.id).subscribe({
      next: () => this.router.navigate(['/users']),
      error: err => {
        this.actionError.set(httpErrorMessage(err, 'Failed to delete user.'));
        this.deleting.set(false);
      },
    });
  }
}
